/*
 * @authors :Bin Mei
 * @date    :2019-03-20
 * @description：Fetch请求处理
 */
import { dmEnv } from '@dm/utils';
import isomorphicFetch from 'isomorphic-fetch';
import { StaticToast, StaticLoading, ThirdLoading } from 'src/components/common';
import { unionPayProcess } from 'src/utils/unionpayProcess';
import validate from 'src/utils/validate';
import cookies from './cookie';
import { xflowPushEvent } from './pageTrack';
import { Deserialize, Serialize } from './serialization';
import { jumpToPage } from './tool';
import { generateTraceInfo } from './traceparent';
import RightLoadingModal from 'src/components/common/RightLoadingModal';
// import * as Sentry from '@sentry/browser';


declare const window: any;

interface Options {
  url: string;
  type: string;
  data?: object;
  action?: any;
  isloading?: boolean;
  headers?: object;
  error?: any;
  success?: any;
  [propName: string]: any;
  needLogin?: boolean;
  needLoading?: boolean;
  requestKey?: string;
  requestHeadersForReport?: Record<string, string>;
  rightLoadingPoll?: boolean;
}

let loadingCount = 0;

// 请求映射表：key -> AbortController[]
const requestMap = new Map<string, AbortController[]>();

/**
 * 取消指定 key 的所有请求
 * @param requestKey 请求标识
 */
export function cancelFetchJson(requestKey: string) {
  const controllers = requestMap.get(requestKey);
  if (controllers && controllers.length > 0) {
    controllers.forEach((controller) => {
      controller.abort();
    });
    requestMap.delete(requestKey);
  }
}

/**
 * 清理已完成的请求（内部使用）
 */
function cleanupRequest(requestKey: string, controller: AbortController) {
  const controllers = requestMap.get(requestKey);
  if (controllers) {
    const index = controllers.indexOf(controller);
    if (index > -1) {
      controllers.splice(index, 1);
    }
    // 如果数组为空，删除 key
    if (controllers.length === 0) {
      requestMap.delete(requestKey);
    }
  }
}

export const fetch = (options: Options) => {
  const { url = '', type, data, isloading = false, needLoading = false, requestKey } = options;
  
  // 创建 AbortController
  const controller = new AbortController();
  
  // 如果有 requestKey，将其加入到映射表中
  if (requestKey) {
    if (!requestMap.has(requestKey)) {
      requestMap.set(requestKey, []);
    }
    requestMap.get(requestKey)!.push(controller);
  }

  if(isloading || needLoading){
    loadingCount++;
    StaticLoading.show();
  }
  const zaLongToken = cookies.get('za_long_token');
  const token = cookies.get('za_token') || (zaLongToken && dmEnv.isApp() ? zaLongToken : '');
  const channelResourceCode = cookies.get('channelResourceCode') || '';
  const channelOrigin = sessionStorage.getItem('channelOrigin') || '';
  const hyChannelCode = sessionStorage.getItem('hyChannelCode') || '';
  const { traceparent } = generateTraceInfo(!!token);

  const requestHeaders = {
    token,
    channelResourceCode,
    channelOrigin,
    hyChannelCode,
    traceparent,
    ...options.headers,
  };
  options.requestHeadersForReport = (({ token, ...rest }) => rest)(requestHeaders);
  const opts: any = {
    credentials: 'include',
    url,
    signal: controller.signal, // 添加 AbortSignal
    headers: requestHeaders,
  };

  if (type.toUpperCase() === 'GET') {
    opts.method = 'GET';
    if (typeof data === 'string') {
      opts.url = `${url}?${data}`;
    } else if (typeof data === 'object') {
      opts.url = `${url}?${Serialize(data)}`;
    }
  }
  if (type.toUpperCase() === 'POST') {
    opts.method = 'POST';
    opts.body = JSON.stringify(data || {});
    opts.headers = {
      ...opts.headers,
      'Content-Type': 'application/json; charset=UTF-8',
    };
  }
  if (type.toUpperCase() === 'UPLOAD') {
    opts.method = 'POST';
    opts.body = data;
  }

  const request = (window.fetch as typeof isomorphicFetch) || isomorphicFetch;

  return request(opts.url, opts)
    .then((req) => {
      // 请求完成后清理
      if (requestKey) {
        cleanupRequest(requestKey, controller);
      }
      return toJson(req, options);
    })
    .then((res) => {
      // 如果是被取消的请求，直接返回，不继续处理
      if (res && res.code === 'ABORTED') {
        return Promise.resolve(res);
      }
      // 请求成功后也需要检查是否被取消（虽然理论上不会发生）
      if (requestKey) {
        cleanupRequest(requestKey, controller);
      }
      return resHandler(res, options);
    })
    .catch((error) => {
      // 请求完成后清理
      if (requestKey) {
        cleanupRequest(requestKey, controller);
      }
      
      // 如果是取消请求，返回一个特殊的 Promise，避免触发错误处理
      if (error.name === 'AbortError' || error.message === 'The user aborted a request.') {
        return Promise.resolve({ code: 'ABORTED', message: '请求已取消' });
      }
      xflowPushEvent(['click', 'FETCH_ERROR', '网络请求异常', { extra_infomation: { error, options } }]);
      return Promise.reject(error);
    });
};

function toJson(resp: any, options: any) {
  // 如果是取消的请求，直接返回
  if (resp.code === 'ABORTED') {
    return Promise.resolve(resp);
  }
  
  if (resp.status >= 400) {
    return errorHandler(resp, options, resp.status);
  }
  return resp.json();
}

export async function fetchJson(options: Options) {
  return fetch(options);
}

// 请求成功处理
function resHandler(res: any, options: any) {
  // 如果是被取消的请求，直接返回，不处理
  if (res && res.code === 'ABORTED') {
    return Promise.resolve(res);
  }
  
  const { isloading = false, needLoading = false, needToast = true, needRedirectLogin = true } = options;
  if(isloading || needLoading){
    loadingCount--;
    !loadingCount && StaticLoading.hide();
  }
  // 直营渠道对接，直接调用对方接口，输出字段不对称，不可走通用逻辑
  if (res && res.returnCode && `${options.url}`.includes('/gwbkApi/v1')) {
    return Promise.resolve(res);
  }
  // 有些情况不需要直接跳转登录页
  if(!needRedirectLogin){
    console.log('needRedirectLogin',needRedirectLogin);
    return Promise.resolve(res);
  }
  if (res && res.code === '20002') {  // 特殊情况没有登录 token重复刷新时
    /**
     * 最福利或众安life环境，使用 sdk 关闭当前webview。让用户能从最福利重新登录
     */
    if (window.iHealthBridge) {
      StaticToast.error('登录已过期，请重新登录');
      setTimeout(() => {
        window.iHealthBridge.goBack?.();
      }, 1000);
      return;
    }
    const loginPage = '/hospital/login';
    const { pathname, search } = window.location;
    if (validate.isAlipayApplet()) {
      my && my.navigateTo({
        url: `/pages/login/index?pathname=${encodeURIComponent(pathname)}&search=${encodeURIComponent(search)}`,
      });
    } else if (validate.isFromUnionPay()) {
      unionPayProcess(pathname, search);
    } else if (validate.isFromMiniApplet()) {
      wx.miniProgram.redirectTo({
        url: `/pages/login?pathname=${encodeURIComponent(pathname)}&search=${encodeURIComponent(search)}`,
      });
    } else {
      // 特殊处理挂号预约的跳转情况
      if (options.url.indexOf('auth/isUserAuth') >= 0) {
        ThirdLoading.hide();
        window.reactHistory.push({
          pathname: loginPage,
          search: `auth=${encodeURIComponent(JSON.stringify(options.data))}`,
        });
      } else {
        if (pathname === '/hospital/init') {
          let { target } = Deserialize(search);
          target = decodeURIComponent(target);
          const [url = '', searchs = ''] = target.split('?');
          jumpToPage({
            history: window.reactHistory,
            pathname: loginPage,
            search: `pathname=${encodeURIComponent(url)}&openId=${options.data.openId}&jumpSearch=${encodeURIComponent(searchs)}`,
            navigateType: 'replace',
          });
        } else {
          if (pathname.indexOf(loginPage) != 0) {
            jumpToPage({
              history: window.reactHistory,
              pathname: loginPage,
              search: `pathname=${encodeURIComponent(pathname)}&jumpSearch=${encodeURIComponent(search)}`,
              navigateType: 'replace',
            });
          }
        }
      }
    }
    return Promise.resolve(res);
  }
  if (res && res.code === '100016') {  // 100016表示渠道没有处于开放时间，不需要弹接口报错提示, 统一跳转过期页面
    window.reactHistory.replace({
      pathname: '/hospital/close',
    });
    xflowPushEvent(['click', 'FETCH_ERROR', `接口请求报错 ${res.code}`, { extra_infomation: { error: res, options } }]);
    return Promise.resolve(res);
  }
  // 针对微甲项目，如果患者已经预约过，重新预约的报错提示有特殊弹窗，所以这里特殊处理一下，把toast跳过
  if (res && res.code === '100004' && options.url === '/api/api/v1/patient/cooperation/weijia/appointment/save') {
    options.error && options.error(res);
    xflowPushEvent(['click', 'FETCH_ERROR', `接口请求报错 ${res.code}`, { extra_infomation: { error: res, options } }]);
    return Promise.resolve(res);
  }
  // 针对领取服务包 跳过toast，直接用接口内的弹窗
  if (res && res.code === '400010' && options.url === '/api/api/v1/patient/user/servpack/draw') {
    options.error && options.error(res);
    xflowPushEvent(['click', 'FETCH_ERROR', `接口请求报错 ${res.code}`, { extra_infomation: { error: res, options } }]);
    return Promise.resolve(res);
  }
  // 权益未生效，弹窗提示并返回上一页
  if (res && res.code === '100028') {
    StaticToast.error(res.message);
    setTimeout(() => {
      window.reactHistory.goBack();
    }, 1500);
    xflowPushEvent(['click', 'FETCH_ERROR', `接口请求报错 ${res.code}`, { extra_infomation: { error: res, options } }]);
    return Promise.resolve(res);
  }
  // 体检套餐已过期，跳转至体检入口页面
  if (res && res.code === '10011') {
    StaticToast.error('预约人信息已过期，请重新预约');
    jumpToPage({
      history: window.reactHistory,
      pathname: '/hospital/sub/szPhysical/physicalEntrance',
      navigateType: 'replace',
      search: '',
    });
    xflowPushEvent(['click', 'FETCH_ERROR', `接口请求报错 ${res.code}`, { extra_infomation: { error: res, options } }]);
    return Promise.resolve(res);
  }
  if (res.status && res.status !== 200) {
    return errorHandler(res.error, options, res.status);
  }
  if (!options.rightLoadingPoll) {
    const intercepted = RightLoadingModal.intercept(res, options);
    if (intercepted) {
      return intercepted;
    }
  }
  if (!res || res.code >= 20000) {
    options.error && options.error(res);
    needToast && StaticToast.error(res.message || '服务异常', 4000);
    xflowPushEvent(['click', 'FETCH_ERROR', `接口请求报错 ${res.code}`, { extra_infomation: { error: res, options } }]);
    console.error(res, 'FETCH_ERROR');
    return Promise.resolve(res);
  }
  options.success && options.success(res);
  return Promise.resolve(res);
}

// 异常处理
async function errorHandler(error: any, options: any, status?: any) {
  const { isloading = false, needLoading = false } = options;
  if(isloading || needLoading){
    loadingCount--;
    !loadingCount && StaticLoading.hide();
  }
  isloading && !loadingCount && StaticLoading.hide();
  options.error && options.error(error);
  StaticToast.error(`网络异常，请稍后重试${status ? `(${status})` : ''}`);
  console.error(error, 'FETCH_ERROR');
  xflowPushEvent(['click', 'FETCH_ERROR', `接口请求报错 ${status}`, { extra_infomation: { error, options } }]);
  return Promise.reject(error);
}

